"use client";

import { localeFlags } from "@/components/flags";
import { defaultLocale, isLocale, locales } from "@/i18n/config";
import { useLocale, useTranslations } from "next-intl";

// Inline toggle showing every locale at once. Both flags stay in full color
// at all times — an explicit product request ("solidarity of nations"): the
// nation's flag is never the thing that gets greyed out. Selection is carried
// by the TR/AZ text alone (active = brand color, inactive = grey). Revisit the
// pattern only when a third language turns this into a dropdown.
// Strips/adds the /az prefix off the real browser path and hard-navigates so
// the proxy re-resolves the locale.
export default function LocaleSwitcher() {
    const rawLocale = useLocale();
    const locale = isLocale(rawLocale) ? rawLocale : defaultLocale;
    const t = useTranslations("localeSwitcher");

    const switchTo = (target: string) => {
        if (target === locale) return;
        // Persist the choice so unprefixed URLs remember it (proxy reads this).
        // Required for tr too, else the proxy would redirect back to /az.
        document.cookie = `NEXT_LOCALE=${target}; path=/; max-age=${60 * 60 * 24 * 365}; samesite=lax`;
        const path = window.location.pathname;
        let base = path;
        for (const l of locales) {
            if (
                l !== defaultLocale &&
                (path === `/${l}` || path.startsWith(`/${l}/`))
            ) {
                base = path.slice(`/${l}`.length) || "/";
                break;
            }
        }
        const dest =
            target === defaultLocale
                ? base
                : `/${target}${base === "/" ? "" : base}`;
        window.location.href = dest + window.location.search;
    };

    return (
        <div
            role="group"
            aria-label={t("label")}
            className="flex items-center gap-1 text-sm font-medium"
        >
            {locales.map((l, i) => {
                const Flag = localeFlags[l];
                const active = l === locale;
                return (
                    <span key={l} className="flex items-center gap-1">
                        {i > 0 && (
                            <span aria-hidden className="text-neutral-300">
                                |
                            </span>
                        )}
                        <button
                            type="button"
                            onClick={() => switchTo(l)}
                            aria-current={active ? "true" : undefined}
                            className={`focus-visible:ring-primary flex items-center gap-1.5 rounded p-1 outline-none focus-visible:ring-2 ${
                                active
                                    ? "text-primary"
                                    : "text-neutral-400 hover:text-neutral-600"
                            }`}
                        >
                            <Flag className="size-4 rounded-full" />
                            <span>{l.toUpperCase()}</span>
                        </button>
                    </span>
                );
            })}
        </div>
    );
}
